'use client';

import Image from 'next/image';
import type React from 'react';
import { useEffect, useRef } from 'react';

interface ServiceData {
  icon: string;
  title: string;
  description: string;
  ariaLabel: string;
}

interface ServiceCardsProps {
  services: ServiceData[];
}

const ServiceCards: React.FC<ServiceCardsProps> = ({ services }) => {
  const cardRefs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    // Set up intersection observer for animations
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            // Add the is-visible class when element is in view
            entry.target.classList.add('is-visible');
            // Once the animation has played, no need to observe anymore
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 },
    );

    // Observe all card elements
    cardRefs.current.forEach((card) => {
      if (card) observer.observe(card);
    });

    // Clean up
    return () => {
      cardRefs.current.forEach((card) => {
        if (card) observer.unobserve(card);
      });
    };
  }, []);

  // Function to set ref for each card
  const setCardRef = (index: number) => (el: HTMLDivElement | null) => {
    if (el) cardRefs.current[index] = el;
  };

  return (
    <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8'>
      {services.map((service, i) => (
        <div
          key={service.title}
          ref={setCardRef(i)}
          className='animate-when-visible'
          style={
            {
              '--animation-delay': `${i * 0.1}s`,
              '--animation-duration': '0.5s',
            } as React.CSSProperties
          }
        >
          <div
            className='relative bg-primary-light/10 dark:bg-primary/10 border border-primary-light/15 dark:border-primary/15 border-b-white dark:border-b-gray-800 border-t-primary-light/10 dark:border-t-primary/10 rounded-2xl shadow-md h-full group hover:bg-primary/5 dark:hover:bg-primary/15 hover:-translate-y-2 hover:shadow-lg transition-all duration-300 flex flex-col items-center text-center p-6 overflow-hidden before:content-[""] before:absolute before:top-0 before:left-0 before:w-full before:h-1 before:bg-linear-to-r before:from-primary before:to-primary-light dark:before:from-primary-light dark:before:to-primary before:scale-x-0 before:origin-left before:transition-transform before:duration-300 hover:before:scale-x-100' // Converted card class to Tailwind
            aria-labelledby={`service-title-${i}`}
          >
            <div className='relative w-16 h-16 mx-auto mb-5 flex items-center justify-center'>
              {' '}
              {/* Adjusted size/margin */}
              <Image
                src={service.icon}
                alt='' // Alt text is decorative here as title provides context
                width={56} // Slightly smaller icon
                height={56}
                className='w-full h-full object-contain group-hover:scale-110 transition-transform duration-300'
                aria-hidden={true}
              />
            </div>
            <h3
              id={`service-title-${i}`}
              className='text-xl font-bold text-primary dark:text-primary-light mb-3 font-serif group-hover:text-primary-dark dark:group-hover:text-primary transition-colors duration-300'
            >
              {service.title}
            </h3>
            <p className='text-gray-700 dark:text-gray-300 font-serif text-sm'>
              {' '}
              {/* Slightly smaller text */}
              {service.description}
            </p>
          </div>
        </div>
      ))}
    </div>
  );
};

export default ServiceCards;
